import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, Dumbbell, Users, Trophy, Clock, Zap, RefreshCw,
  CheckCircle, Calendar, Star, ArrowLeft, UserCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const TRAINING_TYPES = [
  { id: 'personal_trainer', name: 'Personal Trainer Session', icon: UserCheck, defaultDuration: 60, intensity: 'high', color: 'text-purple-400' },
  { id: 'strength_conditioning', name: 'Strength & Conditioning Coach', icon: Dumbbell, defaultDuration: 60, intensity: 'high', color: 'text-red-400' },
  { id: 'team_practice', name: 'Team Practice', icon: Users, defaultDuration: 90, intensity: 'moderate', color: 'text-blue-400' },
  { id: 'private_coaching', name: 'Private Sport Coaching', icon: Star, defaultDuration: 60, intensity: 'moderate', color: 'text-amber-400' },
  { id: 'speed_training', name: 'Speed/Agility Training', icon: Zap, defaultDuration: 45, intensity: 'high', color: 'text-green-400' },
  { id: 'game_competition', name: 'Game/Competition', icon: Trophy, defaultDuration: 90, intensity: 'high', color: 'text-orange-400' },
  { id: 'group_class', name: 'Group Fitness Class', icon: Users, defaultDuration: 45, intensity: 'moderate', color: 'text-pink-400' },
  { id: 'open_gym', name: 'Open Gym/Pickup Games', icon: Dumbbell, defaultDuration: 60, intensity: 'moderate', color: 'text-cyan-400' },
  { id: 'other', name: 'Other Training', icon: Calendar, defaultDuration: 45, intensity: 'moderate', color: 'text-gray-400' }
];

const INTENSITY_OPTIONS = [
  { id: 'light', name: 'Light', description: 'Easy pace, recovery focused', multiplier: '0.5x' },
  { id: 'moderate', name: 'Moderate', description: 'Working but sustainable', multiplier: '1x' },
  { id: 'high', name: 'High', description: 'Challenging, pushing limits', multiplier: '1.5x' },
  { id: 'intense', name: 'Intense', description: 'Maximum effort', multiplier: '2x' }
];

const ExternalWorkoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showLogDialog, setShowLogDialog] = useState(false);
  
  // Form state
  const [trainingType, setTrainingType] = useState('');
  const [customName, setCustomName] = useState('');
  const [duration, setDuration] = useState(60);
  const [intensity, setIntensity] = useState('moderate');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA'));

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/api/athlete-profile/external-training-history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWorkoutHistory(response.data.workouts || []);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (type) => {
    setTrainingType(type.id);
    setDuration(type.defaultDuration);
    setIntensity(type.intensity);
    if (type.id !== 'other') {
      setCustomName('');
    }
  };

  const handleLogWorkout = async () => {
    if (!trainingType) {
      toast.error('Select a training type');
      return;
    }
    
    if (trainingType === 'other' && !customName.trim()) {
      toast.error('Enter a name for this workout');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/api/athlete-profile/log-external-training`, {
        training_type: trainingType,
        custom_name: customName || null,
        duration_minutes: duration,
        intensity: intensity,
        notes: notes,
        date: date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || 'Workout logged!');
      setShowLogDialog(false);
      resetForm();
      fetchHistory();
    } catch (error) {
      toast.error('Failed to log workout');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTrainingType('');
    setCustomName('');
    setDuration(60);
    setIntensity('moderate');
    setNotes('');
    setDate(new Date().toLocaleDateString('en-CA'));
  };

  const calculatePoints = () => {
    const basePoints = (duration / 30) * 10;
    const multipliers = { light: 0.5, moderate: 1.0, high: 1.5, intense: 2.0 };
    return Math.round(basePoints * (multipliers[intensity] || 1.0));
  };

  const getTypeConfig = (typeId) => {
    return TRAINING_TYPES.find(t => t.id === typeId) || TRAINING_TYPES[TRAINING_TYPES.length - 1];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={() => navigate('/training-program')} className="text-aura-text-muted mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Training
            </Button>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              External Training
            </h1>
            <p className="text-aura-text-muted mt-1">
              Log workouts from personal trainers, coaches, and team practice
            </p>
          </div>
          
          <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
            <DialogTrigger asChild>
              <Button className="bg-aura-primary hover:bg-aura-primary-hover">
                <Plus className="w-4 h-4 mr-2" /> Log Workout
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white font-heading uppercase">Log External Workout</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Training Type Selection */}
                <div>
                  <Label className="text-aura-text-muted">Training Type</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {TRAINING_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handleTypeSelect(type)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          trainingType === type.id
                            ? 'border-aura-primary bg-aura-primary/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                      >
                        <type.icon className={`w-5 h-5 mx-auto mb-1 ${type.color}`} />
                        <p className="text-white text-xs">{type.name.split(' ')[0]}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Name (for "other") */}
                {trainingType === 'other' && (
                  <div>
                    <Label className="text-aura-text-muted">Workout Name</Label>
                    <Input
                      placeholder="e.g., Boxing class, Swimming"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                    />
                  </div>
                )}

                {/* Date */}
                <div>
                  <Label className="text-aura-text-muted">Date</Label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                </div>

                {/* Duration */}
                <div>
                  <Label className="text-aura-text-muted">Duration (minutes)</Label>
                  <div className="flex gap-2 mt-1">
                    {[30, 45, 60, 90, 120].map((mins) => (
                      <Button
                        key={mins}
                        variant={duration === mins ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDuration(mins)}
                        className={duration === mins ? "bg-aura-primary" : "border-white/10 text-white"}
                      >
                        {mins}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Intensity */}
                <div>
                  <Label className="text-aura-text-muted">Intensity</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {INTENSITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setIntensity(opt.id)}
                        className={`p-2 rounded-lg border-2 transition-all text-left ${
                          intensity === opt.id
                            ? 'border-aura-primary bg-aura-primary/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-white text-sm font-medium">{opt.name}</p>
                          <Badge className="bg-white/10 text-aura-text-muted text-xs">{opt.multiplier}</Badge>
                        </div>
                        <p className="text-xs text-aura-text-muted">{opt.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-aura-text-muted">Notes (optional)</Label>
                  <Textarea
                    placeholder="What did you work on? How did it feel?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                </div>

                {/* Points Preview */}
                {trainingType && (
                  <div className="p-3 rounded-lg bg-aura-primary/10 border border-aura-primary/30">
                    <div className="flex items-center justify-between">
                      <span className="text-aura-text-muted">Aura Points You'll Earn:</span>
                      <span className="text-2xl font-bold text-aura-primary">+{calculatePoints()}</span>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <Button 
                  onClick={handleLogWorkout}
                  disabled={saving || !trainingType}
                  className="w-full bg-aura-primary hover:bg-aura-primary-hover"
                >
                  {saving ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Logging...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Log Workout</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Dumbbell className="w-6 h-6 text-aura-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{summary.total_sessions}</p>
                <p className="text-xs text-aura-text-muted">Sessions (30 days)</p>
              </CardContent>
            </Card>
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{Math.round(summary.total_duration_minutes / 60)}h</p>
                <p className="text-xs text-aura-text-muted">Total Hours</p>
              </CardContent>
            </Card>
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Star className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{summary.total_aura_points}</p>
                <p className="text-xs text-aura-text-muted">Aura Points Earned</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-gradient-to-br from-aura-primary/10 to-aura-secondary/10 border-aura-primary/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">All Your Training Counts</p>
                <p className="text-sm text-aura-text-muted mt-1">
                  Whether you're working with a personal trainer, S&C coach, or at team practice - 
                  log it here to earn Aura points and track your total training load.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent History */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
              Recent External Workouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workoutHistory.length === 0 ? (
              <div className="text-center py-8">
                <Dumbbell className="w-12 h-12 text-aura-text-muted mx-auto mb-3" />
                <p className="text-white">No external workouts logged yet</p>
                <p className="text-sm text-aura-text-muted mt-1">
                  Start logging your trainer sessions and team practices!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {workoutHistory.map((workout) => {
                  const typeConfig = getTypeConfig(workout.training_type);
                  return (
                    <div 
                      key={workout.workout_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-white/10`}>
                          <typeConfig.icon className={`w-5 h-5 ${typeConfig.color}`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{workout.name}</p>
                          <p className="text-xs text-aura-text-muted">
                            {format(parseISO(workout.date + 'T00:00:00'), 'MMM d')} • {workout.duration_minutes} min • {workout.intensity}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-aura-primary/20 text-aura-primary border-0">
                        +{workout.aura_points} pts
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ExternalWorkoutPage;
